import React, { useState, useRef, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import { DndContext, closestCenter, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import JSZip from 'jszip';
import SafeIcon from './common/SafeIcon';
import NeonBorder from './components/common/NeonBorder';
import ActionButton from './components/common/ActionButton';
import InfoTooltip from './components/common/InfoTooltip';
import SettingsModal from './components/SettingsModal';
import UserManual from './components/UserManual';
import { estimateTokens, estimateCost, formatTokenCount, TOKEN_COSTS } from './utils/tokenCounter';
import { extractAPIEndpoints, extractSymbols, detectTechStack, extractDatabaseSchema, smartSqueeze, scrubPII, generateAsciiTree } from './utils/codeAnalyzer';

const { FiSettings, FiEdit2, FiCopy, FiTrash2, FiPlus, FiBook, FiFolder, FiFileText, FiUpload, FiDownload, FiCheckCircle, FiAlertCircle, FiLoader, FiHash, FiZap, FiShield, FiCode, FiTarget, FiDatabase, FiCheck, FiXCircle, FiEyeOff } = FiIcons;

// Draggable Item Component
const SortableItem = ({ id, item, onEdit, onDuplicate, onDelete, selected, onToggleSelect }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="mb-4 touch-none">
      <NeonBorder className={`bg-white rounded-lg p-4 group ${selected ? 'ring-2 ring-me-orange' : ''}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onToggleSelect(id)}
              className="w-5 h-5 rounded border-me-blue text-me-orange focus:ring-me-orange cursor-pointer"
            />
            <div className="flex items-center gap-4 flex-1 cursor-grab active:cursor-grabbing" {...attributes} {...listeners}>
              <div className="p-3 bg-me-bg rounded-lg text-me-blue">
                <SafeIcon icon={item.type === 'folder' ? FiFolder : FiFileText} className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-me-blue flex items-center">
                  {item.title}
                  <InfoTooltip text={item.description} />
                </h3>
                <p className="text-sm text-gray-500">{item.path}</p>
                {item.size && (
                  <p className="text-xs text-gray-400 mt-1">{item.size}</p>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
            <button onClick={() => onEdit(id)} className="p-2 text-me-blue hover:text-me-orange transition-colors" title="Bearbeiten">
              <SafeIcon icon={FiEdit2} className="w-5 h-5" />
            </button>
            <button onClick={() => onDuplicate(id)} className="p-2 text-me-blue hover:text-me-orange transition-colors" title="Duplizieren">
              <SafeIcon icon={FiCopy} className="w-5 h-5" />
            </button>
            <button onClick={() => onDelete(id)} className="p-2 text-red-400 hover:text-red-600 transition-colors" title="Löschen">
              <SafeIcon icon={FiTrash2} className="w-5 h-5" />
            </button>
          </div>
        </div>
      </NeonBorder>
    </div>
  );
};

function App() {
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [hiddenItems, setHiddenItems] = useState(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedFile, setProcessedFile] = useState(null);
  const [tokenEstimate, setTokenEstimate] = useState(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isManualOpen, setIsManualOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteModalContext, setDeleteModalContext] = useState(null);
  const [settings, setSettings] = useState({
    includeAsciiTree: true,
    includeSymbolIndex: true,
    includeApiMap: true,
    includeDbSchema: true,
    smartSqueeze: false,
    taskPrompt: '',
    targetModel: 'gpt-4',
    outputFormat: 'markdown',
    tokenLimit: 100000,
    tokenLimitUnit: 'tokens',
    liveTokenEstimation: true,
    dependencyCrawler: true,
    techStackAutodetect: true,
    piiScrubber: false,
    gitAwareness: false,
    clipboardExport: false,
    incrementalMode: false,
    focusPreset: 'all'
  });
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const uploadAreaRef = useRef(null);

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('meRepomixSettings');
    if (savedSettings) {
      try {
        setSettings(JSON.parse(savedSettings));
      } catch (e) {
        console.error('Fehler beim Laden der Einstellungen:', e);
      }
    }
  }, []);

  // Save settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('meRepomixSettings', JSON.stringify(settings));
  }, [settings]);

  // Toast auto-dismiss
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleDuplicate = (id) => {
    const itemToClone = items.find(i => i.id === id);
    if (itemToClone) {
      const newItem = { ...itemToClone, id: Date.now().toString(), title: `${itemToClone.title} (Kopie)` };
      setItems([...items, newItem]);
    }
  };

  const getMultiDeleteMode = () => {
    const saved = localStorage.getItem('multiDeleteMode');
    return saved || null;
  };

  const setMultiDeleteMode = (mode) => {
    localStorage.setItem('multiDeleteMode', mode);
  };

  const handleDelete = (id) => {
    if (selectedItems.size > 1) {
      const mode = getMultiDeleteMode();
      if (mode === 'all') {
        performDeleteAll();
      } else if (mode === 'single') {
        performDeleteSingle(id);
      } else {
        setDeleteModalContext({ id });
        setDeleteModalOpen(true);
      }
    } else {
      performDeleteSingle(id);
    }
  };

  const performDeleteSingle = (id) => {
    if (window.confirm('Möchtest du diesen Eintrag wirklich löschen?')) {
      setItems(items.filter(i => i.id !== id));
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
      setHiddenItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const performDeleteAll = () => {
    if (window.confirm(`Möchtest du wirklich ${selectedItems.size} markierte Einträge löschen?`)) {
      setItems(items.filter(i => !selectedItems.has(i.id)));
      setHiddenItems(prev => {
        const newSet = new Set(prev);
        selectedItems.forEach(id => newSet.delete(id));
        return newSet;
      });
      setSelectedItems(new Set());
    }
  };

  const handleDeleteModalChoice = (choice) => {
    setDeleteModalOpen(false);
    setMultiDeleteMode(choice);

    if (choice === 'all') {
      performDeleteAll();
    } else if (choice === 'single') {
      performDeleteSingle(deleteModalContext.id);
    }

    setDeleteModalContext(null);
  };

  const handleEdit = (id) => {
    const item = items.find(i => i.id === id);
    if (!item) return;
    
    const newTitle = prompt("Neuer Name für diesen Eintrag:", item.title);
    if (newTitle) {
      setItems(items.map(i => i.id === id ? { ...i, title: newTitle } : i));
    }
  };

  const handleToggleSelect = (id) => {
    const item = items.find(i => i.id === id);
    if (!item) return;

    setSelectedItems(prev => {
      const newSet = new Set(prev);
      const isCurrentlySelected = newSet.has(id);

      if (isCurrentlySelected) {
        newSet.delete(id);
        if (item.type === 'folder') {
          const folderPath = item.path;
          items.forEach(i => {
            if (i.path.startsWith(folderPath + '/')) {
              newSet.delete(i.id);
            }
          });
        }
      } else {
        newSet.add(id);
        if (item.type === 'folder') {
          const folderPath = item.path;
          items.forEach(i => {
            if (i.path.startsWith(folderPath + '/')) {
              newSet.add(i.id);
            }
          });
        }
      }

      return newSet;
    });
  };

  const handleSelectAll = () => {
    const visibleItems = items.filter(item => !hiddenItems.has(item.id));
    if (selectedItems.size === visibleItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(visibleItems.map(item => item.id)));
    }
  };

  const handleHideSelected = () => {
    if (selectedItems.size === 0) {
      alert('Bitte wähle mindestens eine Datei zum Entfernen aus!');
      return;
    }
    setHiddenItems(prev => new Set([...prev, ...selectedItems]));
    setSelectedItems(new Set());
    showToast(`${selectedItems.size} Element${selectedItems.size !== 1 ? 'e' : ''} aus Auswahl entfernt!`, 'success');
  };

  const processFiles = (files, isFolder = false) => {
    const newItems = [];
    const folders = new Map();
    
    const fileArray = Array.from(files);
    
    fileArray.forEach(file => {
      const path = isFolder ? file.webkitRelativePath : file.name;
      const parts = path.split('/');
      
      if (isFolder) {
        parts.forEach((part, index) => {
          const isLast = index === parts.length - 1;
          const fullPath = parts.slice(0, index + 1).join('/');
          
          if (!folders.has(fullPath)) {
            const id = Date.now().toString() + Math.random();
            const item = {
              id,
              title: isLast ? part : part + '/',
              type: isLast ? 'file' : 'folder',
              path: fullPath,
              size: isLast ? formatFileSize(file.size) : null,
              file: isLast ? file : null,
              description: isLast ? (file.type || 'Datei') : 'Ordner'
            };
            folders.set(fullPath, item);
            newItems.push(item);
          }
        });
      } else {
        const id = Date.now().toString() + Math.random();
        newItems.push({
          id,
          title: file.name,
          type: 'file',
          path: file.name,
          size: formatFileSize(file.size),
          file: file,
          description: file.type || 'Datei'
        });
      }
    });
    
    return newItems;
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (files.length === 0) return;

    const newItems = processFiles(files, false);
    setItems(prev => [...prev, ...newItems]);
    event.target.value = '';
    showToast(`${newItems.length} Datei${newItems.length !== 1 ? 'en' : ''} erfolgreich hochgeladen!`, 'success');
    updateLiveTokenEstimate();
  };

  const handleFolderUpload = async (event) => {
    const files = event.target.files;
    if (files.length === 0) return;

    const newItems = processFiles(files, true);
    setItems(prev => [...prev, ...newItems]);
    event.target.value = '';
    showToast(`${newItems.length} Element${newItems.length !== 1 ? 'e' : ''} aus Ordner hochgeladen!`, 'success');
    updateLiveTokenEstimate();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length === 0) return;

    const hasFolders = Array.from(files).some(file => 
      file.type === '' && file.size === 0 && file.name !== '.DS_Store'
    );

    const newItems = processFiles(files, hasFolders);
    setItems(prev => [...prev, ...newItems]);
    showToast(`${newItems.length} Element${newItems.length !== 1 ? 'e' : ''} per Drag-and-Drop hinzugefügt!`, 'success');
    updateLiveTokenEstimate();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const updateLiveTokenEstimate = async () => {
    if (!settings.liveTokenEstimation) return;
    
    const fileContents = [];
    
    for (const item of items) {
      if (item.file && item.type === 'file') {
        try {
          const content = await readFileContent(item.file);
          fileContents.push({ path: item.path, content });
        } catch (error) {
          console.error('Fehler beim Lesen von:', item.path, error);
        }
      }
    }

    const totalContent = fileContents.map(f => f.content).join('\n');
    const tokens = estimateTokens(totalContent, settings.targetModel);
    const cost = estimateCost(tokens, settings.targetModel, 'input');
    
    setTokenEstimate({
      tokens,
      cost,
      model: settings.targetModel
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleStart = async () => {
    const selectedItemsList = items.filter(item => selectedItems.has(item.id) || selectedItems.size === 0);
    
    if (selectedItemsList.length === 0) {
      alert('Bitte wähle mindestens eine Datei aus!');
      return;
    }

    setIsProcessing(true);
    setProcessedFile(null);

    try {
      await new Promise(resolve => setTimeout(resolve, 500));

      const fileContents = [];
      
      for (const item of selectedItemsList) {
        if (item.file && item.type === 'file') {
          try {
            const content = await readFileContent(item.file);
            fileContents.push({
              path: item.path,
              content: content,
              size: item.size
            });
          } catch (error) {
            console.error('Fehler beim Lesen von:', item.path, error);
          }
        }
      }

      const apiEndpoints = [];
      const allSymbols = [];
      const techStack = detectTechStack(fileContents);
      const dbSchema = extractDatabaseSchema(fileContents);

      fileContents.forEach(file => {
        const endpoints = extractAPIEndpoints(file.content, file.path);
        apiEndpoints.push(...endpoints);
        
        const symbols = extractSymbols(file.content);
        allSymbols.push({ file: file.path, symbols });
      });

      const output = generateRepomixOutput(fileContents, selectedItemsList, {
        apiEndpoints,
        symbols: allSymbols,
        techStack,
        dbSchema
      });

      const tokens = estimateTokens(output, settings.targetModel);
      const cost = estimateCost(tokens, settings.targetModel, 'input');
      
      setTokenEstimate({
        tokens,
        cost,
        model: settings.targetModel
      });

      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      const timestamp = `${year}-${month}-${day}-${hours}${minutes}${seconds}`;
      const filename = `merepomix-wrapper-output-${timestamp}.md`;

      const blob = new Blob([output], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      
      setProcessedFile({
        url,
        filename,
        size: fileContents.length,
        content: output
      });

      showToast(`${fileContents.length} Datei${fileContents.length !== 1 ? 'en' : ''} erfolgreich verarbeitet!`, 'success');

      if (settings.clipboardExport) {
        try {
          await navigator.clipboard.writeText(output);
          showToast(`Output in die Zwischenablage kopiert! (${output.length} Zeichen)`, 'success');
        } catch (err) {
          console.error('Clipboard-Export fehlgeschlagen:', err);
        }
      }

    } catch (error) {
      console.error('Fehler bei der Verarbeitung:', error);
      showToast('Fehler bei der Verarbeitung: ' + error.message, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  };

  const generateRepomixOutput = (fileContents, allItems, analysis) => {
    let output = '';
    
    if (settings.outputFormat === 'xml') {
      output = generateXmlOutput(fileContents, allItems, analysis);
    } else if (settings.outputFormat === 'json') {
      output = JSON.stringify(generateJsonOutput(fileContents, allItems, analysis), null, 2);
    } else {
      output = generateMarkdownOutput(fileContents, allItems, analysis);
    }
    
    return output;
  };

  const generateMarkdownOutput = (fileContents, allItems, analysis) => {
    let output = `# Repomix Output\n\n`;
    
    if (settings.taskPrompt) {
      output += `## 📋 Aufgabe (Task)\n\n${settings.taskPrompt}\n\n`;
      output += `---\n\n`;
    }
    
    output += `## 📊 Projektübersicht\n\n`;
    output += `**Erstellt am:** ${new Date().toLocaleString('de-DE')}\n`;
    output += `**Anzahl Dateien:** ${fileContents.length}\n`;
    output += `**Gesamtstruktur:** ${allItems.length} Elemente\n`;
    output += `**Ziel-Modell:** ${settings.targetModel}\n\n`;
    
    if (settings.techStackAutodetect && analysis.techStack) {
      output += `### 🔧 Tech Stack (Technologie-Stapel)\n\n`;
      const ts = analysis.techStack;
      if (ts.frameworks.length > 0) output += `**Frameworks:** ${ts.frameworks.join(', ')}\n`;
      if (ts.languages.length > 0) output += `**Sprachen:** ${ts.languages.join(', ')}\n`;
      if (ts.databases.length > 0) output += `**Datenbanken:** ${ts.databases.join(', ')}\n`;
      if (ts.tools.length > 0) output += `**Tools:** ${ts.tools.join(', ')}\n`;
      output += `\n`;
    }
    
    output += `---\n\n`;
    
    if (settings.includeAsciiTree) {
      output += `## 🌳 Dateistruktur (ASCII-Baum)\n\n\`\`\`\n`;
      output += generateAsciiTree(allItems);
      output += `\n\`\`\`\n\n`;
    }
    
    if (settings.includeApiMap && analysis.apiEndpoints && analysis.apiEndpoints.length > 0) {
      output += `## 🔌 API-Endpunkte\n\n`;
      analysis.apiEndpoints.forEach(endpoint => {
        output += `- **${endpoint.method}** \`${endpoint.path}\` (${endpoint.framework}) - ${endpoint.file}\n`;
      });
      output += `\n`;
    }
    
    if (settings.includeDbSchema && analysis.dbSchema && analysis.dbSchema.length > 0) {
      output += `## 🗄️ Datenbank-Schema\n\n`;
      analysis.dbSchema.forEach(schema => {
        output += `### ${schema.file}\n`;
        output += `Tabellen: ${schema.tables.map(t => `\`${t}\``).join(', ')}\n\n`;
      });
    }
    
    output += `---\n\n`;
    
    output += `## 📄 Dateiinhalte\n\n`;
    
    fileContents.forEach(file => {
      let content = file.content;
      
      if (settings.smartSqueeze) {
        content = smartSqueeze(content);
      }
      
      if (settings.piiScrubber) {
        content = scrubPII(content);
      }
      
      output += `### ${file.path}\n\n`;
      output += `**Größe:** ${file.size}\n\n`;
      
      if (settings.includeSymbolIndex) {
        const symbols = analysis.symbols?.find(s => s.file === file.path)?.symbols;
        if (symbols && (symbols.classes.length > 0 || symbols.functions.length > 0)) {
          output += `**Inhalt:**\n`;
          if (symbols.classes.length > 0) output += `- Klassen: ${symbols.classes.join(', ')}\n`;
          if (symbols.functions.length > 0) output += `- Funktionen: ${symbols.functions.join(', ')}\n`;
          output += `\n`;
        }
      }
      
      output += `\`\`\`\n${content}\n\`\`\`\n\n`;
      output += `---\n\n`;
    });
    
    output += `## 💡 Hinweise für LLMs\n\n`;
    output += `Diese Datei wurde mit meRepomix-Wrapper erstellt. Sie enthält die vollständige Projektstruktur und Inhalte.\n`;
    output += `Analysiere zuerst die Struktur, dann arbeite dich durch die Dateiinhalte.\n`;
    output += `\n`;
    output += `### Einstellungen:\n`;
    output += `- Target Model: ${settings.targetModel}\n`;
    output += `- Smart Squeeze: ${settings.smartSqueeze ? 'Aktiv' : 'Inaktiv'}\n`;
    output += `- PII Scrubber: ${settings.piiScrubber ? 'Aktiv' : 'Inaktiv'}\n`;
    
    return output;
  };

  const generateXmlOutput = (fileContents, allItems, analysis) => {
    let xml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
    xml += `<document_content>\n`;
    xml += `  <metadata>\n`;
    xml += `    <created_at>${new Date().toISOString()}</created_at>\n`;
    xml += `    <file_count>${fileContents.length}</file_count>\n`;
    xml += `    <target_model>${settings.targetModel}</target_model>\n`;
    
    if (settings.taskPrompt) {
      xml += `    <task>${settings.taskPrompt}</task>\n`;
    }
    
    xml += `  </metadata>\n`;
    
    if (settings.includeAsciiTree) {
      xml += `  <file_structure>\n`;
      xml += `    <ascii_tree>\n`;
      xml += generateAsciiTree(allItems).split('\n').map(line => `      ${line}`).join('\n');
      xml += `\n    </ascii_tree>\n`;
      xml += `  </file_structure>\n`;
    }
    
    xml += `  <files>\n`;
    
    fileContents.forEach(file => {
      let content = file.content;
      
      if (settings.smartSqueeze) {
        content = smartSqueeze(content);
      }
      
      if (settings.piiScrubber) {
        content = scrubPII(content);
      }
      
      xml += `    <file path="${file.path}" size="${file.size}">\n`;
      xml += `      <content><![CDATA[${content}]]></content>\n`;
      xml += `    </file>\n`;
    });
    
    xml += `  </files>\n`;
    xml += `</document_content>`;
    
    return xml;
  };

  const generateJsonOutput = (fileContents, allItems, analysis) => {
    return {
      metadata: {
        created_at: new Date().toISOString(),
        file_count: fileContents.length,
        target_model: settings.targetModel,
        task: settings.taskPrompt || null
      },
      file_structure: settings.includeAsciiTree ? generateAsciiTree(allItems) : null,
      files: fileContents.map(file => {
        let content = file.content;
        
        if (settings.smartSqueeze) {
          content = smartSqueeze(content);
        }
        
        if (settings.piiScrubber) {
          content = scrubPII(content);
        }
        
        return {
          path: file.path,
          size: file.size,
          content: content
        };
      })
    };
  };

  const downloadProcessedFile = () => {
    if (!processedFile) return;
    
    const link = document.createElement('a');
    link.href = processedFile.url;
    link.download = processedFile.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('Download gestartet!', 'success');
  };

  const clearAll = () => {
    if (window.confirm('Möchtest du wirklich alle Einträge löschen?')) {
      setItems([]);
      setSelectedItems(new Set());
      setHiddenItems(new Set());
      setProcessedFile(null);
      setTokenEstimate(null);
      showToast('Alle Einträge gelöscht!', 'success');
    }
  };

  const handleProjectBackup = () => {
    const backupData = {
      version: "1.0",
      timestamp: new Date().toISOString(),
      settings: settings,
      items: items.map(item => ({
        id: item.id,
        title: item.title,
        type: item.type,
        path: item.path,
        size: item.size,
        description: item.description,
        hasFile: !!item.file
      }))
    };
    
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const timestamp = `${year}-${month}-${day}-${hours}${minutes}${seconds}`;
    
    const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `merepomix-project-backup-${timestamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast('Projekt-Backup erstellt (Metadaten)', 'success');
  };

  const handleProjectImport = (data) => {
    if (data.settings) setSettings(data.settings);
    if (data.items) {
      setItems(data.items.map(item => ({
        ...item,
        file: null
      })));
      showToast('Projekt importiert. Dateien müssen neu verknüpft werden.', 'success');
    }
  };

  const activeItem = items.find(item => item.id === activeId);
  const visibleItems = items.filter(item => !hiddenItems.has(item.id));

  return (
    <div className="min-h-screen bg-me-bg text-gray-800 font-sans pb-20">
      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-50 px-8 py-4 rounded-xl shadow-2xl animate-bounce-in">
          <div className={`flex items-center gap-4 ${toast.type === 'success' ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-red-500 to-rose-600'} text-white`}>
            {toast.type === 'success' ? (
              <SafeIcon icon={FiCheck} className="w-10 h-10" />
            ) : (
              <SafeIcon icon={FiXCircle} className="w-10 h-10" />
            )}
            <span className="text-xl font-bold">{toast.message}</span>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <NeonBorder className="w-full max-w-2xl bg-white shadow-2xl rounded-lg">
            <div className="p-8 bg-white rounded-lg">
              <h2 className="text-3xl font-bold text-me-blue mb-6 text-center">
                Meinst du nur diesen Eintrag oder alle markierten Einträge?
              </h2>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => handleDeleteModalChoice('all')}
                  className="px-8 py-4 bg-me-orange text-white rounded-lg font-bold text-lg hover:bg-me-orange/90 transition-colors shadow-md"
                >
                  Alle markierten Einträge
                </button>
                <button
                  onClick={() => handleDeleteModalChoice('single')}
                  className="px-8 py-4 bg-me-blue text-white rounded-lg font-bold text-lg hover:bg-me-blue/90 transition-colors shadow-md"
                >
                  Nur dieser Eintrag
                </button>
              </div>
            </div>
          </NeonBorder>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b-2 border-me-orange shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://meinerfahrungsbericht.de/logo-me/me-logo.png" 
              alt="me Logo" 
              className="h-12 w-auto object-contain"
            />
            <h1 className="text-2xl font-bold text-me-blue hidden sm:block">
              meRepomix-Wrapper
            </h1>
          </div>
          
          <div className="flex items-center gap-4">
             <button 
              onClick={() => setIsManualOpen(true)}
              className="flex items-center gap-2 text-me-blue hover:text-me-orange font-medium"
            >
              <SafeIcon icon={FiBook} />
              <span className="hidden sm:inline">Handbuch</span>
            </button>

            <button 
              onClick={() => setIsSettingsOpen(true)}
              className="p-3 bg-gray-100 rounded-full hover:bg-me-orange hover:text-white transition-all duration-300 shadow-sm"
              title="Einstellungen"
            >
              <SafeIcon icon={FiSettings} className="w-6 h-6 animate-[spin_10s_linear_infinite] hover:animate-none" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-me-blue mb-4">Projekt vorbereiten</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Lade dein Projekt hoch und erstelle eine LLM-optimierte Ausgabe (eine Datei, die Künstliche Intelligenzen gut verstehen können).
            <InfoTooltip text="Hier lädst du deine Dateien hoch, damit sie für KIs vorbereitet werden können." />
          </p>
        </div>

        {/* Token Estimate Display */}
        {tokenEstimate && (
          <div className="mb-8 p-6 bg-white rounded-lg border-2 border-me-blue/20 shadow-md">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-me-bg rounded-full flex items-center justify-center">
                <SafeIcon icon={FiHash} className="w-6 h-6 text-me-orange" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-me-blue mb-1">Token-Schätzung</h3>
                <p className="text-sm text-gray-600">
                  <span className="font-semibold text-me-orange">{formatTokenCount(tokenEstimate.tokens)} Tokens</span>
                  {tokenEstimate.cost > 0 && (
                    <span className="ml-2">(~ ${tokenEstimate.cost.toFixed(4)} USD)</span>
                  )}
                  <span className="ml-2 text-gray-400">für {tokenEstimate.model}</span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Upload Area with Drag-and-Drop */}
        <div 
          ref={uploadAreaRef}
          className="mb-12 space-y-4"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <NeonBorder className={`bg-white p-8 rounded-xl transition-all duration-300 ${isDragOver ? 'ring-4 ring-me-orange scale-105' : ''}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center gap-3 p-6 bg-me-bg rounded-lg hover:bg-me-orange/10 transition-colors group"
              >
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <SafeIcon icon={FiUpload} className="w-8 h-8 text-me-orange" />
                </div>
                <div className="text-center">
                  <h3 className="font-bold text-me-blue">Dateien hochladen</h3>
                  <p className="text-sm text-gray-500">Einzelne Dateien auswählen</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </button>

              <button
                onClick={() => folderInputRef.current?.click()}
                className="flex flex-col items-center gap-3 p-6 bg-me-bg rounded-lg hover:bg-me-orange/10 transition-colors group"
              >
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <SafeIcon icon={FiFolder} className="w-8 h-8 text-me-orange" />
                </div>
                <div className="text-center">
                  <h3 className="font-bold text-me-blue">Ordner hochladen</h3>
                  <p className="text-sm text-gray-500">Ganzen Projektordner wählen</p>
                </div>
                <input
                  ref={folderInputRef}
                  type="file"
                  webkitdirectory=""
                  directory=""
                  multiple
                  onChange={handleFolderUpload}
                  className="hidden"
                />
              </button>
            </div>
            
            {isDragOver && (
              <div className="mt-4 text-center p-4 bg-me-orange/10 rounded-lg border-2 border-me-orange/30 border-dashed">
                <p className="text-me-orange font-bold text-lg">
                  <SafeIcon icon={FiUpload} className="inline mr-2" />
                  Loslassen zum Hochladen!
                </p>
              </div>
            )}
          </NeonBorder>

          {items.length > 0 && (
            <div className="flex justify-center">
              <button
                onClick={clearAll}
                className="text-sm text-red-500 hover:text-red-700 underline"
              >
                Alle Einträge löschen
              </button>
            </div>
          )}
        </div>

        {/* Status Display */}
        {isProcessing && (
          <div className="mb-8 text-center p-6 bg-white rounded-lg border-2 border-me-orange/20">
            <SafeIcon icon={FiLoader} className="w-12 h-12 text-me-orange mx-auto mb-3 animate-spin" />
            <h3 className="text-lg font-bold text-me-blue mb-2">Verarbeite Dateien...</h3>
            <p className="text-sm text-gray-600">Dies kann einen Moment dauern</p>
          </div>
        )}

        {processedFile && (
          <div className="mb-8 p-6 bg-white rounded-lg border-2 border-green-500/20">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-me-blue mb-1">Fertig!</h3>
                <p className="text-sm text-gray-600">
                  {processedFile.size} Dateien verarbeitet
                </p>
              </div>
              <ActionButton onClick={downloadProcessedFile} variant="primary">
                <SafeIcon icon={FiDownload} />
                Download
              </ActionButton>
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-me-blue mb-2">Aktive Features:</h4>
              <div className="flex flex-wrap gap-2">
                {settings.includeAsciiTree && (
                  <span className="px-2 py-1 bg-me-bg text-me-blue text-xs rounded-full flex items-center gap-1">
                    <SafeIcon icon={FiCode} /> ASCII-Baum
                  </span>
                )}
                {settings.includeSymbolIndex && (
                  <span className="px-2 py-1 bg-me-bg text-me-blue text-xs rounded-full flex items-center gap-1">
                    <SafeIcon icon={FiHash} /> Symbol-Index
                  </span>
                )}
                {settings.includeApiMap && (
                  <span className="px-2 py-1 bg-me-bg text-me-blue text-xs rounded-full flex items-center gap-1">
                    <SafeIcon icon={FiZap} /> API-Map
                  </span>
                )}
                {settings.includeDbSchema && (
                  <span className="px-2 py-1 bg-me-bg text-me-blue text-xs rounded-full flex items-center gap-1">
                    <SafeIcon icon={FiDatabase} /> DB-Schema
                  </span>
                )}
                {settings.smartSqueeze && (
                  <span className="px-2 py-1 bg-me-bg text-me-blue text-xs rounded-full flex items-center gap-1">
                    <SafeIcon icon={FiZap} /> Smart-Squeeze
                  </span>
                )}
                {settings.piiScrubber && (
                  <span className="px-2 py-1 bg-me-bg text-me-blue text-xs rounded-full flex items-center gap-1">
                    <SafeIcon icon={FiShield} /> PII-Scrubber
                  </span>
                )}
                {settings.techStackAutodetect && (
                  <span className="px-2 py-1 bg-me-bg text-me-blue text-xs rounded-full flex items-center gap-1">
                    <SafeIcon icon={FiTarget} /> Tech-Detect
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* List Area */}
        {items.length > 0 && (
          <div className="space-y-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-4">
                <h3 className="text-xl font-bold text-me-blue">
                  Dateistruktur ({visibleItems.length} Einträge)
                </h3>
                <button
                  onClick={handleSelectAll}
                  className="text-sm text-me-blue hover:text-me-orange underline"
                >
                  {selectedItems.size === visibleItems.length && visibleItems.length > 0 ? 'Alle abwählen' : 'Alle auswählen'}
                </button>
                {selectedItems.size > 0 && (
                  <>
                    <span className="text-sm text-gray-500">
                      ({selectedItems.size} ausgewählt)
                    </span>
                    <button
                      onClick={handleHideSelected}
                      className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-bold text-base hover:from-red-600 hover:to-red-700 transition-all shadow-lg flex items-center gap-2 animate-pulse-slow"
                    >
                      <SafeIcon icon={FiEyeOff} className="w-5 h-5" />
                      Aus Auswahl entfernen
                    </button>
                  </>
                )}
              </div>
              <ActionButton onClick={handleStart} variant="primary" disabled={isProcessing}>
                {isProcessing ? (
                  <>
                    <SafeIcon icon={FiLoader} className="animate-spin mr-2" />
                    Verarbeite...
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiPlus} />
                    Starten
                  </>
                )}
              </ActionButton>
            </div>

            <DndContext 
              collisionDetection={closestCenter} 
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={visibleItems} strategy={verticalListSortingStrategy}>
                {visibleItems.map((item) => (
                  <SortableItem 
                    key={item.id} 
                    id={item.id} 
                    item={item} 
                    onEdit={handleEdit}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                    selected={selectedItems.has(item.id)}
                    onToggleSelect={handleToggleSelect}
                  />
                ))}
              </SortableContext>
              <DragOverlay>
                {activeItem ? (
                  <div className="opacity-50 cursor-grabbing">
                    <NeonBorder className="bg-white rounded-lg p-4">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-me-bg rounded-lg text-me-blue">
                          <SafeIcon icon={activeItem.type === 'folder' ? FiFolder : FiFileText} className="w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="font-bold text-me-blue">{activeItem.title}</h3>
                          <p className="text-sm text-gray-500">{activeItem.path}</p>
                        </div>
                      </div>
                    </NeonBorder>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        )}

        {items.length === 0 && !isProcessing && !processedFile && (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-me-bg rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiAlertCircle} className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-me-blue mb-2">Noch keine Dateien</h3>
            <p className="text-gray-500">Lade Dateien oder einen Ordner oben hoch (per Klick oder Drag-and-Drop), um zu beginnen</p>
          </div>
        )}
      </main>

      {/* Modals */}
      <SettingsModal 
        isOpen={isSettingsOpen} 
        onClose={() => setIsSettingsOpen(false)}
        settings={settings}
        onSettingsChange={setSettings}
        onProjectBackup={handleProjectBackup}
        onProjectImport={handleProjectImport}
      />
      <UserManual isOpen={isManualOpen} onClose={() => setIsManualOpen(false)} />
    </div>
  );
}

export default App;